import React, {
  useState, useContext, useEffect, useCallback
} from 'react'
import { useLazyQuery } from '@apollo/react-hooks'
import { path, prop, compose } from 'ramda'
import NextEpisodeAndUserRatingQuery from './next-episode-and-user-rating.gql'
import NextEpisodeCountdown from './next-episode-countdown'
import { StateContext } from '../../state'

import { convertData } from '../../utils/episode-selection'
import { shouldShowBanner } from './utils/should-show-banner'
import { getDetailsUrl } from '../../../../lib/utils'
import MoreLikeThis from './more-like-this'
import PlayerBannerRateModal from '../../../../modules/user-rating/player-banner-rate-modal'
import { UserRatingScale } from '../../../../modules/user-rating/shared'

const getNextEpisode = compose(prop('nextEpisode'), convertData)

function Banner() {
  // when a modal has been dismissed by user, shouldShow* set to false
  const [shouldShowRate, setShouldShowRate] = useState(false)
  const [shouldShowMoreLikeThis, setShouldShowMoreLikeThis] = useState(true)
  const [shouldShowNextEpisode, setShouldShowNextEpisode] = useState(true)

  const [hasDecidedToShowRate, setHasDecidedToShowRate] = useState(false)

  const dismissRateModal = useCallback(() => {
    setShouldShowRate(false)
  }, [])
  const dismissMoreLikeThis = useCallback(() => {
    setShouldShowMoreLikeThis(false)
  }, [])
  const dismissNextEpisodeCountdown = useCallback(() => {
    setShouldShowNextEpisode(false)
  }, [])

  const {
    getMetaData,
    duration,
    currentTime,
    isContinueWatchingShown,
    contentId: contentIdFromState,
    getStreams,
    values,
    isTrailer,
    saveCurrentPosition,
    isPlaying,
    contentItem,
    setIsBannerRateModalShowing,
    setIsNextEpisodeCountdownShowing,
    isAdOnPauseShown
  } = useContext(StateContext)

  const [
    fetchData,
    { loading, error, data: episodeAndRatingData }
  ] = useLazyQuery(NextEpisodeAndUserRatingQuery)

  const countdownTime = values.PLAYER_AUTO_PLAY_COUNTDOWN
  const countdownThreshold = values.PLAYER_AUTO_PLAY_THRESHOLD
  /**
   * if shouldRenderBanner is false, all children of Banner (PlayerBannerRateModal, MoreLikeThis, NextEpisodeCountdown) won't show, and NextEpisodeAndUserRatingQuery won't be fired
   */
  const shouldRenderBanner = shouldShowBanner(
    getMetaData,
    duration,
    currentTime,
    isContinueWatchingShown,
    countdownThreshold,
    countdownTime,
    isAdOnPauseShown
  )

  useEffect(() => {
    if (shouldRenderBanner) {
      fetchData({
        variables: {
          contentId: contentIdFromState,
          userRatingInput: {
            contentItemId:
              contentItem.__typename === 'Episode'
                ? contentItem.series.id
                : contentItem.id
          }
        }
      })
      saveCurrentPosition('STOP')
    }
  }, [shouldRenderBanner, contentIdFromState, contentItem])

  if (isTrailer || !shouldRenderBanner) return null

  if (!shouldShowRate && !shouldShowMoreLikeThis && !shouldShowNextEpisode) {
    return null
  }

  // wait for response of NextEpisodeAndUserRatingQuery
  if (loading || error || !episodeAndRatingData) return null

  const seriesOrMovie =
    contentItem.__typename === 'Episode' ? contentItem.series : contentItem

  const isMovie = !path(['contentItem', 'series'])(episodeAndRatingData)
  const showpageUrl = getDetailsUrl(episodeAndRatingData.contentItem)
  const userRating = episodeAndRatingData.userRating
  const isMovieOrSeriesRated = userRating && userRating.ratingValue !== UserRatingScale.UNRATED
  const isMovieOrSeriesRatedBad = isMovieOrSeriesRated && userRating.ratingValue === UserRatingScale.NOT_MY_THING

  // when episodeAndRatingData is fetched for the first time, remember whether we have decided to show rate based on isMovieOrSeriesRated,
  // and the following change of isMovieOrSeriesRated(after user rated on the modal which will change episodeAndRatingData) won't affect shouldShowRate,
  // after user dismissed the rate modal, shouldShowRate will be set to false
  if (!hasDecidedToShowRate) {
    if (!isMovieOrSeriesRated) {
      setShouldShowRate(true)
    }
    setHasDecidedToShowRate(true)
    return null // prevent reaching further rendering logic as we want a rerender due to change of shouldShowRate
  }

  if (isMovie) {
    if (shouldShowRate) {
      return (
        <PlayerBannerRateModal
          setIsBannerRateModalShowing={setIsBannerRateModalShowing}
          dismissRateModal={dismissRateModal}
          contentItem={seriesOrMovie}
        />
      )
    }
    if (!shouldShowRate && shouldShowMoreLikeThis && !isMovieOrSeriesRatedBad) {
      return (
        <MoreLikeThis
          showpageUrl={showpageUrl}
          dismissMoreLikeThis={dismissMoreLikeThis}
        />
      )
    }
  } else {
    const nextEpisode = getNextEpisode(episodeAndRatingData)
    if (nextEpisode && shouldShowNextEpisode) {
      let displayCountdownTime = countdownTime
      const roundedCurrentTime = Math.floor(currentTime)
      if (duration - roundedCurrentTime < displayCountdownTime) {
        displayCountdownTime = duration - roundedCurrentTime
      }
      return (
        <NextEpisodeCountdown
          nextEpisode={nextEpisode}
          currentEpisodeInfo={convertData(episodeAndRatingData).current}
          getStreams={getStreams}
          countdownTime={displayCountdownTime}
          dismissBanner={dismissNextEpisodeCountdown}
          setIsNextEpisodeCountdownShowing={setIsNextEpisodeCountdownShowing}
          isPlaying={isPlaying}
        />
      )
    }
    if (!nextEpisode && shouldShowRate) {
      return (
        <PlayerBannerRateModal
          setIsBannerRateModalShowing={setIsBannerRateModalShowing}
          dismissRateModal={dismissRateModal}
          contentItem={seriesOrMovie}
        />
      )
    }
    if (!nextEpisode && !shouldShowRate && shouldShowMoreLikeThis && !isMovieOrSeriesRatedBad) {
      return (
        <MoreLikeThis
          showpageUrl={showpageUrl}
          dismissMoreLikeThis={dismissMoreLikeThis}
        />
      )
    }
  }

  return null
}

export default Banner
